import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/common/Sidebar";
import { 
  forgotVSUserPassword, 
  verifyResetOTP, 
  resetPasswordWithOTP 
} from "../services/vsUserService";
import "./Login.css";

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Steps: 'normal', 'request-otp', 'verify-otp', 'new-password', 'success'
  const [step, setStep] = React.useState(location.state?.focusForgot ? 'request-otp' : 'normal');
  const [email, setEmail] = React.useState(location.state?.forgotEmail || "");
  const [otp, setOtp] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await forgotVSUserPassword({ email });
      if (response.isOk) {
        setStep('verify-otp');
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await verifyResetOTP({ email, otp });
      if (response.isOk) {
        setStep('new-password');
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP or expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const response = await resetPasswordWithOTP({ email, otp, newPassword });
      if (response.isOk) {
        setSuccess("Password reset successful! You can now login from the sidebar.");
        setStep('success');
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content-area">
        <section className="login-message-panel">
          <div className="login-header">
            <h2>
              {step === 'normal' ? 'Access Required' : 'Reset Password'}
            </h2>
          </div>
          <div className="login-body">
            {step === 'normal' && (
              <div className="login-step-content">
                <p style={{ color: "#cfabdd", fontSize: "14px", padding: "20px" }}>
                  Please contact the administrator or check your email for authorization details.
                </p>
                <div style={{ textAlign: "center", paddingBottom: "20px" }}>
                  <button 
                    className="reset-btn-link" 
                    onClick={() => setStep('request-otp')}
                  >
                    Forgot Password? Click here to reset via OTP
                  </button>
                </div>
              </div>
            )}

            {step === 'request-otp' && (
              <form className="reset-form" onSubmit={handleRequestOTP}>
                <p className="reset-hint">Enter your registered email to receive a 6-digit OTP.</p>
                {error && <p className="reset-error">{error}</p>}
                <div className="reset-row">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required 
                    placeholder="Enter your email"
                  />
                </div>
                <div className="reset-actions">
                  <button type="submit" className="reset-submit-btn" disabled={loading}>
                    {loading ? "Sending..." : "Send OTP"}
                  </button>
                  <button type="button" className="reset-cancel-btn" onClick={() => setStep('normal')}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {step === 'verify-otp' && (
              <form className="reset-form" onSubmit={handleVerifyOTP}>
                <p className="reset-hint">We've sent a 6-digit OTP to <strong>{email}</strong>.</p>
                {error && <p className="reset-error">{error}</p>}
                <div className="reset-row">
                  <label>Enter OTP</label>
                  <input 
                    type="text" 
                    value={otp} 
                    onChange={(e) => setOtp(e.target.value)} 
                    required 
                    maxLength="6"
                    placeholder="6-digit code"
                    style={{ letterSpacing: "5px", textAlign: "center", fontSize: "20px" }}
                  />
                </div>
                <div className="reset-actions">
                  <button type="submit" className="reset-submit-btn" disabled={loading}>
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button type="button" className="reset-cancel-btn" onClick={() => setStep('request-otp')}>
                    Back
                  </button>
                </div>
              </form>
            )}

            {step === 'new-password' && (
              <form className="reset-form" onSubmit={handleResetPassword}>
                <p className="reset-hint">OTP verified! Now enter your new password.</p>
                {error && <p className="reset-error">{error}</p>}
                <div className="reset-row">
                  <label>New Password</label>
                  <input 
                    type="password" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    required 
                    placeholder="Minimum 6 characters"
                  />
                </div>
                <div className="reset-row">
                  <label>Confirm Password</label>
                  <input 
                    type="password" 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    required 
                    placeholder="Re-enter password"
                  />
                </div>
                <div className="reset-actions">
                  <button type="submit" className="reset-submit-btn" disabled={loading}>
                    {loading ? "Resetting..." : "Update Password"}
                  </button>
                </div>
              </form>
            )}

            {step === 'success' && (
              <div className="reset-success-container">
                <p className="reset-success-msg">{success}</p>
                <button className="reset-submit-btn" onClick={() => navigate("/")}>
                  Go to Home
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
